/**
 * Fishing Stats Panel
 * Right sidebar: shows level/XP, stat summary with expandable breakdowns,
 * and stat allocation UI.
 */

import { useState, useMemo } from 'react';
import { useGameStore } from '@/state';
import { EntityStats } from '@/types';
import { computeAllStatBreakdowns, StatBreakdown } from '@/utils';
import { useStatAllocation } from '@/hooks/useStatAllocation';

type StatKey = 'catchChance' | 'fishingTime' | 'qualityBonus' | 'valueBonus';

const STAT_LABELS: Record<StatKey, string> = {
  catchChance: 'Catch Chance',
  fishingTime: 'Fishing Time',
  qualityBonus: 'Quality Bonus',
  valueBonus: 'Value Bonus',
};

const CHARACTER_STATS: Array<{ key: keyof EntityStats; name: string; icon: string; description: string }> = [
  { key: 'eyesight', name: 'Eyesight', icon: '👁️', description: 'Catch speed + catch rate' },
  { key: 'reflexes', name: 'Reflexes', icon: '⚡', description: 'Catch chance' },
  { key: 'finesse', name: 'Finesse', icon: '✨', description: 'Fish quality' },
  { key: 'physique', name: 'Physique', icon: '💪', description: 'Catch speed + fish value' },
  { key: 'luck', name: 'Luck', icon: '🍀', description: 'Small bonus to all stats' },
];

export function FishingStatsPanel() {
  const [expandedStat, setExpandedStat] = useState<StatKey | null>(null);

  const progression = useGameStore((s) => s.playerState.progression);
  const equippedGear = useGameStore((s) => s.playerState.equippedGear);
  const { handleAllocateStat } = useStatAllocation();

  const { stats, progress } = progression;
  const xpPercentage = (progress.currentXP / progress.xpToNextLevel) * 100;
  const hasUnspentPoints = progress.unspentStatPoints > 0;

  const breakdowns = useMemo(
    () => computeAllStatBreakdowns(equippedGear, stats),
    [equippedGear, stats],
  );

  return (
    <div className="p-3 space-y-3">
      {/* Level Header */}
      <div className="bg-gray-800/60 rounded-lg p-3 border border-purple-500/30">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-2">
            <span className="text-xl">⭐</span>
            <div>
              <span className="font-bold text-purple-300 text-sm">Level {progress.level}</span>
              <span className="text-[10px] text-gray-400 ml-2">
                {progress.currentXP} / {progress.xpToNextLevel} XP
              </span>
            </div>
          </div>
          {hasUnspentPoints && (
            <div className="bg-yellow-600 text-white px-2 py-0.5 rounded-full text-[10px] font-bold animate-pulse">
              {progress.unspentStatPoints}pt{progress.unspentStatPoints > 1 ? 's' : ''}
            </div>
          )}
        </div>
        <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
            style={{ width: `${xpPercentage}%` }}
          />
        </div>
      </div>

      {/* Fishing Stats — 2-column grid */}
      <div className="space-y-1">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">
          Fishing Stats
        </h3>
        <div className="grid grid-cols-2 gap-1">
          {(Object.keys(STAT_LABELS) as StatKey[]).map((key) => (
            <StatSummaryRow
              key={key}
              statKey={key}
              label={STAT_LABELS[key]}
              breakdown={breakdowns[key]}
              isExpanded={expandedStat === key}
              onToggle={() => setExpandedStat(expandedStat === key ? null : key)}
            />
          ))}
        </div>
      </div>

      {/* Character Stats — 2-column grid */}
      <div className="space-y-1">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">
          Character Stats
          {hasUnspentPoints && (
            <span className="text-yellow-400 ml-1 normal-case">
              ({progress.unspentStatPoints} point{progress.unspentStatPoints > 1 ? 's' : ''})
            </span>
          )}
        </h3>
        <div className="grid grid-cols-2 gap-1">
          {CHARACTER_STATS.map(({ key, name, icon }) => (
            <div
              key={key}
              className="flex items-center justify-between bg-gray-800/40 rounded p-2"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-sm">{icon}</span>
                <span className="text-xs font-medium text-white">{name}</span>
                <span className="text-xs text-purple-400 font-bold">{stats[key]}</span>
              </div>
              {hasUnspentPoints && (
                <button
                  onClick={() => handleAllocateStat(key)}
                  className="px-1.5 py-0.5 bg-green-600 hover:bg-green-700 rounded text-[10px] font-bold transition-colors min-h-[24px] min-w-[24px] shrink-0 ml-1"
                >
                  +1
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface StatSummaryRowProps {
  statKey: StatKey;
  label: string;
  breakdown: StatBreakdown;
  isExpanded: boolean;
  onToggle: () => void;
}

function StatSummaryRow({ label, breakdown, isExpanded, onToggle }: StatSummaryRowProps) {
  const hasSources = breakdown.sources.length > 0 || breakdown.multipliers.length > 0;

  return (
    <div className={isExpanded ? 'col-span-2' : ''}>
      <button
        onClick={hasSources ? onToggle : undefined}
        className={`w-full flex flex-col rounded p-2 text-left transition-colors ${
          hasSources ? 'hover:bg-gray-700/40 cursor-pointer' : 'cursor-default'
        } ${isExpanded ? 'bg-gray-700/40' : 'bg-gray-800/40'}`}
      >
        <span className="text-[10px] text-gray-400 leading-tight">{label}</span>
        <div className="flex items-center gap-1">
          <span className="text-xs font-bold text-white">{breakdown.formattedFinal}</span>
          {hasSources && (
            <span className="text-gray-500 text-[10px]">{isExpanded ? '▾' : '▸'}</span>
          )}
        </div>
      </button>

      {/* Expanded breakdown */}
      {isExpanded && hasSources && (
        <div className="mt-1 mb-1 bg-gray-800/60 rounded p-2 border border-gray-700/50 space-y-0.5">
          {/* Additive sources */}
          {breakdown.sources.map((source, i) => (
            <div key={i} className="flex items-center justify-between text-[10px]">
              <span className="flex items-center gap-1 text-gray-400">
                {source.icon && <span>{source.icon}</span>}
                <span>{source.label}</span>
              </span>
              <span className="text-gray-300 font-mono">{source.formattedValue}</span>
            </div>
          ))}

          {/* Separator if both sources and multipliers */}
          {breakdown.sources.length > 0 && breakdown.multipliers.length > 0 && (
            <div className="border-t border-gray-700/50 my-1" />
          )}

          {/* Multiplier sources */}
          {breakdown.multipliers.map((mult, i) => (
            <div key={i} className="flex items-center justify-between text-[10px]">
              <span className="flex items-center gap-1 text-blue-400/70">
                {mult.icon && <span>{mult.icon}</span>}
                <span>{mult.label}</span>
              </span>
              <span className="text-blue-300/70 font-mono">{mult.formattedValue}</span>
            </div>
          ))}

          {/* Final */}
          <div className="border-t border-gray-600/50 mt-1 pt-1 flex items-center justify-between text-[10px]">
            <span className="text-gray-300 font-medium">Final</span>
            <span className="text-white font-bold font-mono">{breakdown.formattedFinal}</span>
          </div>
        </div>
      )}
    </div>
  );
}
